import {
  BoundDateField,
  BoundNumberField,
  BoundSelectField,
  Button,
  CollapseToggle,
  Css,
  DateField,
  FormLines,
  GridColumn,
  GridDataRow,
  GridTable,
  Icon,
  IconButton,
  Palette,
  SimpleHeaderAndData,
  TestIds,
  Tooltip,
  actionColumn,
  collapseColumn,
  column,
  emptyCell,
  simpleHeader,
  useComputed,
  useModal,
  useSnackbar,
  useTestIds,
} from "@homebound/beam";
import { ListFieldState, ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { useMemo } from "react";
import { FormattedDate } from "src/components";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import {
  FeatureFlagType,
  GlobalPlanMilestoneType,
  IncrementalCollectionOp,
  InputMaybe,
  PlanMilestoneTargetInput,
  SavePlanScheduleInput,
  SystemPlanMilestone,
} from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { CopyMilestoneTargetsModal } from "src/routes/projects/settings/CopyMilestoneTargetsModal";
import {
  ScheduleTab_GlobalPlanMilestoneFragment,
  ScheduleTab_PlanScheduleFragment,
  usePlanScheduleTabQuery,
  useSavePlanScheduleSettingsMutation,
} from "src/routes/projects/settings/ScheduleTab.generated";
import { isDefined, noop, pluralize, queryResult } from "src/utils";
import { Temporal } from "temporal-polyfill";

type PlanScheduleTabProps = { isCommunity: boolean };

export function PlanScheduleTab(props: PlanScheduleTabProps) {
  const { isCommunity } = props;
  const { projectId } = useRouteParams<{ projectId: string }>();
  const query = usePlanScheduleTabQuery({ variables: { projectId } });
  return queryResult(query, {
    data: (data) => {
      const { planSchedule } = data.project;
      // All H1 hierarchy projects should get created with a `PlanSchedule`, but this is not guaranteed
      if (!planSchedule) return <></>;
      return (
        <PlanScheduleTabView
          planSchedule={planSchedule}
          globalPlanMilestones={data.globalPlanMilestones.entities}
          isCommunity={isCommunity}
        />
      );
    },
  });
}

type PlanScheduleTabViewProps = {
  planSchedule: ScheduleTab_PlanScheduleFragment;
  globalPlanMilestones: ScheduleTab_GlobalPlanMilestoneFragment[];
  isCommunity: boolean;
};

export function PlanScheduleTabView(props: PlanScheduleTabViewProps) {
  const { planSchedule, globalPlanMilestones, isCommunity } = props;
  const projectId = planSchedule.parent.id;
  const scheduleMilestoneTargetsEnabled = useFeatureFlag(FeatureFlagType.ScheduleMilestoneTargets);
  const tid = useTestIds({}, "planScheduleSettings");
  const { triggerNotice } = useSnackbar();
  const { openModal } = useModal();
  const [savePlanSchedule, { loading: saving }] = useSavePlanScheduleSettingsMutation();

  const formState = useFormState({
    config: planScheduleFormConfig,
    init: {
      input: planSchedule,
      map: (input) => ({
        id: input.id,
        targetStartDate: input.targetStartDate,
        targetCycleTimeInDays: input.targetCycleTimeInDays ?? 0,
        currentBaselineDate: input.currentBaselineDate,
        targetConstructionRtsDate: input.targetConstructionRtsDate,
        targetPreconRtsDate: input.targetPreconRtsDate,
        milestoneTargets: mapMilestoneTargetsToFormValue(input, isCommunity, scheduleMilestoneTargetsEnabled),
      }),
    },
  });

  const months = useComputed(() => {
    if (!formState.targetCycleTimeInDays.value) return 0;
    // Round to nearest 0.1 month
    return Math.round((formState.targetCycleTimeInDays.value / 30.0) * 10) / 10;
  }, [formState.targetCycleTimeInDays.value]);

  async function save() {
    const input: SavePlanScheduleInput = {
      id: formState.id.value,
      targetStartDate: formState.targetStartDate.value,
      targetCycleTimeInDays: formState.targetCycleTimeInDays.value,
      currentBaselineDate: formState.currentBaselineDate.value,
    };

    if (scheduleMilestoneTargetsEnabled) {
      const planMilestoneTargets = formState.milestoneTargets.rows.flatMap((row) => mapRowToSaveInput(row));
      if (planMilestoneTargets.nonEmpty) {
        input.planMilestoneTargets = planMilestoneTargets;
      }
      if (!isCommunity) {
        input.targetConstructionRtsDate = formState.targetConstructionRtsDate.value;
        input.targetPreconRtsDate = formState.targetPreconRtsDate.value;
      }
    }

    await savePlanSchedule({
      variables: { input },
      // Clear cache on `planScheduleSnapshots` so we don't keep stale values when `currentBaselineDate` changes
      update: (cache) => cache.evict({ fieldName: "planScheduleSnapshots" }),
      onCompleted: () => {
        formState.commitChanges();
        triggerNotice({ message: "Schedule configuration has been saved." });
      },
    });
  }

  function onSave() {
    if (
      isCommunity &&
      scheduleMilestoneTargetsEnabled &&
      shouldConfirmCommunityAutoScale(formState, globalPlanMilestones)
    ) {
      openModal({
        content: (
          <ConfirmationModal
            title="Auto-scale structure milestones?"
            label="Save"
            confirmationMessage="Saving changes to construction Target Days from RTS will auto-scale construction milestone targets on structures in this community."
            onConfirmAction={save}
          />
        ),
      });
      return;
    }
    void save();
  }

  const { title, description, width } = isCommunity
    ? {
        title: "Community Schedule Configuration",
        description:
          "Set up the schedule inputs and target dates for this community schedule (but not the projects within this community).",
        width: 680,
      }
    : { title: "Schedule Configuration", description: "Set up the schedule inputs and target dates.", width: 940 };

  return (
    <Observer>
      {() => (
        <div data-testid="planScheduleSettings" css={Css.wPx(width).$}>
          <div css={Css.bgWhite.br8.bshBasic.p2.pr3.$}>
            <div css={Css.df.jcsb.ais.mb3.gap1.$}>
              <div>
                <h2 css={Css.xl.$}>{title}</h2>
                <p css={Css.mt1.gray700.$}>{description}</p>
              </div>
              <div css={Css.df.gap1.$}>
                <Button
                  disabled={saving || !formState.dirty}
                  label="Cancel"
                  variant="tertiary"
                  onClick={() => formState.revertChanges()}
                  {...tid.cancel}
                />
                <Button
                  disabled={saving || !formState.dirty || !formState.valid}
                  label="Save"
                  onClick={onSave}
                  {...tid.save}
                />
              </div>
            </div>
            <FormLines>
              <h3 css={Css.lg.$}>Schedule Settings</h3>
              <div css={Css.mb3.df.gap2.$}>
                <BoundDateField label="Start Date" field={formState.targetStartDate} disabledDays={isWeekend} />
                <div>
                  <div css={Css.df.$}>
                    <BoundNumberField
                      label="Target Cycle Time"
                      field={formState.targetCycleTimeInDays}
                      onChange={(value) => {
                        formState.targetCycleTimeInDays.set(value);
                        if (!isCommunity && scheduleMilestoneTargetsEnabled) {
                          scaleMilestoneTargetsToCycleTime(formState.milestoneTargets, value, globalPlanMilestones);
                        }
                      }}
                    />
                    <Tooltip title="month is estimated at 30 days">
                      <div css={Css.wPx(220).asfe.pl1.sm.$}>
                        <p>calendar days</p>
                        <p>
                          ({months} {pluralize(months, "month")})
                        </p>
                      </div>
                    </Tooltip>
                  </div>
                  {!isCommunity && scheduleMilestoneTargetsEnabled && (
                    <p css={Css.mt1.xs.gray700.$} {...tid.targetCycleTimeHelperText}>
                      Auto-scales construction milestone targets
                    </p>
                  )}
                </div>
              </div>
              <h3 css={Css.lg.$}>Target Dates</h3>
              {!isCommunity && scheduleMilestoneTargetsEnabled && (
                <div css={Css.mb3.df.gap2.$}>
                  <BoundDateField
                    label="Target Preconstruction RTS"
                    field={formState.targetPreconRtsDate}
                    disabledDays={isWeekend}
                  />
                  <BoundDateField
                    label="Target Construction RTS"
                    field={formState.targetConstructionRtsDate}
                    disabledDays={isWeekend}
                  />
                </div>
              )}
              <div css={Css.mb3.df.gap2.$}>
                <BoundDateField
                  label="Current Baseline"
                  field={formState.currentBaselineDate}
                  disabledDays={isWeekend}
                />
                <DateField
                  label="Target End Date"
                  value={planSchedule.targetEndDate}
                  readOnly
                  onChange={noop}
                  disabledDays={isWeekend}
                />
              </div>
            </FormLines>
            {scheduleMilestoneTargetsEnabled && (
              <MilestoneTargetConfigurationTable
                projectId={projectId}
                formState={formState}
                globalPlanMilestones={globalPlanMilestones}
                isCommunity={isCommunity}
              />
            )}
          </div>
        </div>
      )}
    </Observer>
  );
}

type MilestoneTargetRow = PlanMilestoneTargetInput & {
  globalPlanMilestoneName?: InputMaybe<string>;
  communityTargetDaysFromRts?: InputMaybe<number>;
  communityTargetCycleTimeInDays?: InputMaybe<number>;
  currentEndDate?: InputMaybe<Temporal.PlainDate>;
};

type PlanScheduleFormValue = Pick<
  SavePlanScheduleInput,
  | "id"
  | "targetStartDate"
  | "targetCycleTimeInDays"
  | "currentBaselineDate"
  | "targetPreconRtsDate"
  | "targetConstructionRtsDate"
> & { milestoneTargets: MilestoneTargetRow[] };

type MilestoneTargetGroupRow = { kind: "group"; id: string; data: { name: string } };
type MilestoneTargetGridRow = SimpleHeaderAndData<ObjectState<MilestoneTargetRow>> | MilestoneTargetGroupRow;

type MilestoneTargetConfigurationTableProps = {
  projectId: string;
  formState: ObjectState<PlanScheduleFormValue>;
  globalPlanMilestones: ScheduleTab_GlobalPlanMilestoneFragment[];
  isCommunity: boolean;
};

function MilestoneTargetConfigurationTable(props: MilestoneTargetConfigurationTableProps) {
  const { projectId, formState, globalPlanMilestones, isCommunity } = props;
  const { milestoneTargets } = formState;
  const { openModal } = useModal();
  const tid = useTestIds({}, "milestoneTargetConfiguration");

  const { visibleRows, canAddRow, canResetAll } = useComputed(() => {
    const visibleRows = milestoneTargets.rows.filter((row) => isVisibleMilestoneTargetRow(row, globalPlanMilestones));
    const targetCycleTimeInDays = formState.targetCycleTimeInDays.value;
    const communityVerticalCompleteTdf = getCommunityVerticalCompleteTdf(visibleRows, globalPlanMilestones);

    return {
      visibleRows,
      canAddRow: isCommunity && !visibleRows.some((row) => !row.globalPlanMilestoneId.value),
      canResetAll: visibleRows.some((row) =>
        canResetRowToScaledCommunityDefault(
          row,
          targetCycleTimeInDays,
          globalPlanMilestones,
          communityVerticalCompleteTdf,
        ),
      ),
    };
  }, [formState]);

  const columns = useMemo(
    () => createMilestoneTargetColumns({ formState, globalPlanMilestones, isCommunity, tid }),
    [formState, globalPlanMilestones, isCommunity, tid],
  );

  const rows = createMilestoneTargetRows(visibleRows, globalPlanMilestones);

  function addRow() {
    // Force a selection on the first "pending" row before allowing a new to be added
    if (hasPendingRow(visibleRows)) return;
    milestoneTargets.add({ op: IncrementalCollectionOp.Include });
  }

  function resetAllToCommunityDefaults() {
    const targetCycleTimeInDays = formState.targetCycleTimeInDays.value;
    const communityVerticalCompleteTdf = getCommunityVerticalCompleteTdf(visibleRows, globalPlanMilestones);
    visibleRows.forEach((row) =>
      resetRowToScaledCommunityDefault(row, targetCycleTimeInDays, globalPlanMilestones, communityVerticalCompleteTdf),
    );
  }

  function openCopyModal() {
    openModal({
      content: <CopyMilestoneTargetsModal projectId={projectId} milestoneTargetsField={milestoneTargets} />,
    });
  }

  const description = `Set the expected number of days from RTS to each milestone to establish the ${isCommunity ? "community's default " : ""}target schedule.`;
  const tooltip = isCommunity
    ? "New milestone targets will be copied down to all projects in the community. Milestone targets are in calendar days, not work days."
    : "This is pulled from the Community settings. Editing these project milestone targets will not affect the community defaults.";

  return (
    <div data-testid="milestoneTargetConfiguration" css={Css.mt3.$}>
      <div css={Css.df.aic.gap1.mb1.$}>
        <h3 css={Css.xl.$}>Milestone Target Configuration</h3>
        <Icon icon="infoCircle" color={Palette.Gray700} inc={2} tooltip={tooltip} />
      </div>
      <p css={Css.mb3.gray700.$}>{description}</p>
      <GridTable columns={columns} rows={rows} style={{ bordered: true, allWhite: true }} />
      <div css={Css.df.gap2.mt2.$}>
        {isCommunity && (
          <>
            <Button icon="plus" variant="tertiary" label="Add" onClick={addRow} disabled={!canAddRow} {...tid.add} />
            <Button icon="duplicate" variant="tertiary" label="Copy" onClick={openCopyModal} {...tid.copy} />
          </>
        )}
        {!isCommunity && (
          <Button
            icon="refresh"
            variant="tertiary"
            label="Reset All"
            onClick={resetAllToCommunityDefaults}
            disabled={!canResetAll}
            tooltip="Reset all milestone targets to the Community default"
            {...tid.resetAll}
          />
        )}
      </div>
    </div>
  );
}

type CreateMilestoneTargetColumnsProps = {
  formState: ObjectState<PlanScheduleFormValue>;
  globalPlanMilestones: ScheduleTab_GlobalPlanMilestoneFragment[];
  isCommunity: boolean;
  tid: TestIds;
};

function createMilestoneTargetColumns(props: CreateMilestoneTargetColumnsProps): GridColumn<MilestoneTargetGridRow>[] {
  const { formState, globalPlanMilestones, isCommunity, tid } = props;
  const { milestoneTargets } = formState;
  const groupColspan = isCommunity ? 3 : 6;
  const communityVerticalCompleteTdf = getCommunityVerticalCompleteTdf(milestoneTargets.rows, globalPlanMilestones);

  const collapseCol = collapseColumn<MilestoneTargetGridRow>({
    header: emptyCell,
    group: (_, { row }) => ({ content: () => <CollapseToggle row={row} /> }),
    data: emptyCell,
    w: "40px",
  });

  const milestoneColumn = column<MilestoneTargetGridRow>({
    header: "Milestone",
    group: ({ name }) => ({ content: () => name, typeScale: "smSb", colspan: groupColspan }),
    data: (row) =>
      isCommunity ? (
        <Observer>
          {() => (
            <BoundSelectField
              field={row.globalPlanMilestoneId}
              labelStyle="hidden"
              placeholder="Milestone Name"
              options={getMilestoneOptions(row, milestoneTargets, globalPlanMilestones)}
              onSelect={(globalPlanMilestoneId) =>
                selectMilestone(row, globalPlanMilestoneId, milestoneTargets, globalPlanMilestones)
              }
              disabled={
                isDefined(row.id.value)
                  ? "Cannot be changed after saving. Delete this row and add a new one to change the milestone."
                  : undefined
              }
            />
          )}
        </Observer>
      ) : (
        <Observer>
          {() => (
            <div css={Css.df.aic.gap1.$}>
              <span css={Css.mw0.$}>{row.globalPlanMilestoneName.value}</span>
              {isVerticalCompleteMilestone(row, globalPlanMilestones) && (
                <Icon
                  icon="infoCircle"
                  color={Palette.Gray700}
                  inc={2}
                  xss={Css.fs0.$}
                  tooltip="This milestone's target days value is directly tied to the structure cycle time and cannot be manually set."
                />
              )}
              {isSalesReadyMilestone(row, globalPlanMilestones) && (
                <Icon
                  icon="infoCircle"
                  color={Palette.Gray700}
                  inc={2}
                  xss={Css.fs0.$}
                  tooltip="This milestone's target days will not be auto-scaled by the structure cycle time."
                />
              )}
              {row.targetDaysFromRtsSetByUser.value && (
                <Icon
                  icon="pin"
                  color={Palette.Gray700}
                  inc={2}
                  xss={Css.fs0.$}
                  tooltip="This value was set by a User and will be ignored by milestone auto-scaling."
                />
              )}
            </div>
          )}
        </Observer>
      ),
    w: 2,
  });

  const targetDaysColumn = column<MilestoneTargetGridRow>({
    header: "Target Days from RTS",
    group: emptyCell,
    data: (row) => (
      <BoundNumberField
        field={row.targetDaysFromRts}
        onChange={(value) => {
          row.targetDaysFromRts.set(value);
          // TDF set by user is only relevant for non-community projects.
          if (!isCommunity) {
            row.targetDaysFromRtsSetByUser.set(true);
          }
        }}
        disabled={!isCommunity && isVerticalCompleteMilestone(row, globalPlanMilestones)}
        labelStyle="hidden"
        positiveOnly
        placeholder="00"
      />
    ),
    w: "160px",
  });

  const scaledDefaultColumn = column<MilestoneTargetGridRow>({
    header: () => ({
      content: "Default Value",
      tooltip: (
        <>
          <div>
            <b>Preconstruction:</b> The default community value
          </div>
          <div>
            <b>Construction:</b> Calculated value based on the ratio of the community and structure Vertical Complete
            target days
          </div>
        </>
      ),
    }),
    group: emptyCell,
    data: (row) => (
      <span>
        {getCommunityDefaultTargetDaysFromRts(
          row,
          formState.targetCycleTimeInDays.value,
          globalPlanMilestones,
          communityVerticalCompleteTdf,
        ) ?? "N/A"}
      </span>
    ),
    w: "160px",
  });

  const targetEndDateColumn = column<MilestoneTargetGridRow>({
    header: "Target End Date",
    group: emptyCell,
    data: (row) => (
      <Observer>
        {() => (
          <FormattedDate
            date={getDisplayTargetEndDate({ row, formState, globalPlanMilestones })}
            dateFormatStyle="short"
            mdash
          />
        )}
      </Observer>
    ),
    w: 1,
  });

  const currentEndDateColumn = column<MilestoneTargetGridRow>({
    header: "Current End Date",
    group: emptyCell,
    data: (row) => (
      <Observer>
        {() => {
          const targetEndDate = getDisplayTargetEndDate({ row, formState, globalPlanMilestones });
          const isOffTrack =
            isDefined(row.currentEndDate.value) &&
            isDefined(targetEndDate) &&
            row.currentEndDate.value.isAfter(targetEndDate);
          return (
            <FormattedDate
              date={row.currentEndDate.value ?? undefined}
              dateFormatStyle="short"
              mdash
              xss={isOffTrack ? Css.red500.$ : undefined}
            />
          );
        }}
      </Observer>
    ),
    w: 1,
  });

  const actionsColumn = actionColumn<MilestoneTargetGridRow>({
    header: "Actions",
    group: emptyCell,
    data: (row) =>
      isCommunity ? (
        <IconButton icon="trash" onClick={() => removeRow(milestoneTargets, row)} {...tid.deleteMilestoneTarget} />
      ) : (
        <Observer>
          {() => (
            <IconButton
              icon="refresh"
              onClick={() =>
                resetRowToScaledCommunityDefault(
                  row,
                  formState.targetCycleTimeInDays.value,
                  globalPlanMilestones,
                  communityVerticalCompleteTdf,
                )
              }
              disabled={
                !canResetRowToScaledCommunityDefault(
                  row,
                  formState.targetCycleTimeInDays.value,
                  globalPlanMilestones,
                  communityVerticalCompleteTdf,
                )
              }
              tooltip="Reset to default value"
              {...tid.resetMilestoneTarget}
            />
          )}
        </Observer>
      ),
    w: "80px",
  });

  if (isCommunity) {
    return [collapseCol, milestoneColumn, targetDaysColumn, actionsColumn];
  }

  return [
    collapseCol,
    milestoneColumn,
    targetDaysColumn,
    scaledDefaultColumn,
    targetEndDateColumn,
    currentEndDateColumn,
    actionsColumn,
  ];
}

function createMilestoneTargetRows(
  visibleRows: ObjectState<MilestoneTargetRow>[],
  globalPlanMilestones: ScheduleTab_GlobalPlanMilestoneFragment[],
): GridDataRow<MilestoneTargetGridRow>[] {
  const pendingRows = visibleRows.filter((row) => !row.globalPlanMilestoneId.value);
  // Enums don't have ids so we don't have a stable object identity for groupByObject to work with
  const typeByCode = Object.fromEntries(globalPlanMilestones.map((milestone) => [milestone.type.code, milestone.type]));

  const groupRows = visibleRows
    .filter(
      (row) =>
        row.globalPlanMilestoneId.value && getMilestoneType(row.globalPlanMilestoneId.value!, globalPlanMilestones),
    )
    .groupByObject((row) => typeByCode[getMilestoneType(row.globalPlanMilestoneId.value!, globalPlanMilestones)!.code])
    .map(([type, rows]) => ({
      kind: "group" as const,
      id: type.code,
      data: { name: `${type.name} Milestones` },
      children: rows.map((row) => ({ kind: "data" as const, id: getGridRowId(row), data: row })),
    }));

  return [
    simpleHeader,
    ...groupRows,
    ...pendingRows.map((row) => ({ kind: "data" as const, id: getGridRowId(row), data: row })),
  ] as GridDataRow<MilestoneTargetGridRow>[];
}

function getMilestoneType(
  globalPlanMilestoneId: string,
  globalPlanMilestones: ScheduleTab_GlobalPlanMilestoneFragment[],
) {
  return globalPlanMilestones.find((milestone) => milestone.id === globalPlanMilestoneId)?.type;
}

function isSalesReadyMilestone(
  row: ObjectState<MilestoneTargetRow>,
  globalPlanMilestones: ScheduleTab_GlobalPlanMilestoneFragment[],
) {
  return (
    globalPlanMilestones.find((milestone) => milestone.id === row.globalPlanMilestoneId.value)?.systemPlanMilestone
      ?.code === SystemPlanMilestone.SalesReady
  );
}

function isVerticalCompleteMilestone(
  row: ObjectState<MilestoneTargetRow>,
  globalPlanMilestones: ScheduleTab_GlobalPlanMilestoneFragment[],
) {
  return (
    globalPlanMilestones.find((milestone) => milestone.id === row.globalPlanMilestoneId.value)?.systemPlanMilestone
      ?.code === SystemPlanMilestone.VerticalComplete
  );
}

function getCommunityVerticalCompleteTdf(
  rows: readonly ObjectState<MilestoneTargetRow>[],
  globalPlanMilestones: ScheduleTab_GlobalPlanMilestoneFragment[],
) {
  const vcRow = rows.find((row) => isVerticalCompleteMilestone(row, globalPlanMilestones));
  return vcRow?.communityTargetDaysFromRts.value ?? undefined;
}

function isVisibleMilestoneTargetRow(
  row: ObjectState<MilestoneTargetRow>,
  globalPlanMilestones: ScheduleTab_GlobalPlanMilestoneFragment[],
) {
  if (row.op.value === IncrementalCollectionOp.Delete) return false;
  // globalPlanMilestones only loads PRECONSTRUCTION/CONSTRUCTION. A target may still reference a milestone
  // that was selected from that list but later had its type changed in the catalog — hide those rows.
  // Unsaved rows with no milestone selected yet are always kept.
  if (!row.globalPlanMilestoneId.value) return true;
  return getMilestoneType(row.globalPlanMilestoneId.value, globalPlanMilestones) !== undefined;
}

function mapMilestoneTargetsToFormValue(
  planSchedule: ScheduleTab_PlanScheduleFragment,
  isCommunity: boolean,
  scheduleMilestoneTargetsEnabled: boolean,
): MilestoneTargetRow[] {
  if (!scheduleMilestoneTargetsEnabled) {
    return [];
  }

  const milestoneTargets: MilestoneTargetRow[] = planSchedule.planMilestoneTargets.map((target) => ({
    id: target.id,
    globalPlanMilestoneId: target.globalPlanMilestone.id,
    globalPlanMilestoneName: target.globalPlanMilestone.name,
    targetDaysFromRts: target.targetDaysFromRts,
    targetDaysFromRtsSetByUser: target.targetDaysFromRtsSetByUser,
    op: IncrementalCollectionOp.Include,
    communityTargetCycleTimeInDays: planSchedule.communityTargetCycleTimeInDays,
    communityTargetDaysFromRts: target.communityTarget?.targetDaysFromRts,
    currentEndDate: target.planMilestone?.estEndDate,
  }));

  // Empty state: init with a row so the user doesn't need to click "Add" first
  if (isCommunity && milestoneTargets.isEmpty) {
    milestoneTargets.push({ op: IncrementalCollectionOp.Include });
  }

  return milestoneTargets;
}

function hasMilestoneTargetChanges(row: ObjectState<MilestoneTargetRow>) {
  return row.targetDaysFromRts.dirty || row.globalPlanMilestoneId.dirty || row.op.dirty;
}

/** Community construction TDF changes push auto-scaled construction targets down to structures. */
function shouldConfirmCommunityAutoScale(
  formState: ObjectState<PlanScheduleFormValue>,
  globalPlanMilestones: ScheduleTab_GlobalPlanMilestoneFragment[],
) {
  return formState.milestoneTargets.rows.some((row) => {
    if (row.op.value === IncrementalCollectionOp.Delete || !row.targetDaysFromRts.dirty) return false;
    if (
      getMilestoneType(row.globalPlanMilestoneId.value!, globalPlanMilestones)?.code !==
      GlobalPlanMilestoneType.Construction
    ) {
      return false;
    }
    // Sales Handoff and Vertical Complete are not auto-scaled onto structures
    return !isSalesReadyMilestone(row, globalPlanMilestones) && !isVerticalCompleteMilestone(row, globalPlanMilestones);
  });
}

function mapRowToSaveInput(row: ObjectState<MilestoneTargetRow>): PlanMilestoneTargetInput[] {
  if (row.op.value === IncrementalCollectionOp.Delete) {
    return row.id.originalValue ? [{ id: row.id.value, op: IncrementalCollectionOp.Delete }] : [];
  }
  // Omit unchanged persisted rows from the save payload
  if (row.id.originalValue && !hasMilestoneTargetChanges(row)) {
    return [];
  }
  return [
    {
      id: row.id.value,
      globalPlanMilestoneId: row.globalPlanMilestoneId.value,
      targetDaysFromRts: row.targetDaysFromRts.value,
      targetDaysFromRtsSetByUser: row.targetDaysFromRtsSetByUser.value,
      op: IncrementalCollectionOp.Include,
    },
  ];
}

type GetDisplayTargetEndDateProps = {
  row: ObjectState<MilestoneTargetRow>;
  formState: ObjectState<PlanScheduleFormValue>;
  globalPlanMilestones: ScheduleTab_GlobalPlanMilestoneFragment[];
};

function getDisplayTargetEndDate(props: GetDisplayTargetEndDateProps) {
  const { row, formState, globalPlanMilestones } = props;
  const rtsDate = getRtsDateForMilestone(
    row.globalPlanMilestoneId.value,
    globalPlanMilestones,
    formState.targetPreconRtsDate.value,
    formState.targetConstructionRtsDate.value,
  );
  if (isDefined(rtsDate) && isDefined(row.targetDaysFromRts.value)) {
    // RTS is day 1, so "N days from RTS" means add N - 1 calendar days
    return rtsDate.add({ days: row.targetDaysFromRts.value - 1 });
  }
  return undefined;
}

function getRtsDateForMilestone(
  globalPlanMilestoneId: InputMaybe<string>,
  globalPlanMilestones: ScheduleTab_GlobalPlanMilestoneFragment[],
  targetPreconRtsDate: InputMaybe<Temporal.PlainDate>,
  targetConstructionRtsDate: InputMaybe<Temporal.PlainDate>,
) {
  if (!globalPlanMilestoneId) return undefined;

  const milestoneType = globalPlanMilestones.find((milestone) => milestone.id === globalPlanMilestoneId)?.type.code;
  if (milestoneType === GlobalPlanMilestoneType.Preconstruction) return targetPreconRtsDate;
  if (milestoneType === GlobalPlanMilestoneType.Construction) return targetConstructionRtsDate;
  return undefined;
}

function canResetRowToScaledCommunityDefault(
  row: ObjectState<MilestoneTargetRow>,
  targetCycleTimeInDays: InputMaybe<number>,
  globalPlanMilestones: ScheduleTab_GlobalPlanMilestoneFragment[],
  communityVerticalCompleteTdf: InputMaybe<number>,
) {
  if (isVerticalCompleteMilestone(row, globalPlanMilestones)) return false;

  const communityDefault = getCommunityDefaultTargetDaysFromRts(
    row,
    targetCycleTimeInDays,
    globalPlanMilestones,
    communityVerticalCompleteTdf,
  );
  return isDefined(communityDefault) && row.targetDaysFromRts.value !== communityDefault;
}

/**
 * Because this form is not autosaved and the user can re-add the same milestone that has a WIP deleted entity,
 * we have to first check if we can revive the deleted row instead of adding a duplicate.
 */
function selectMilestone(
  row: ObjectState<MilestoneTargetRow>,
  globalPlanMilestoneId: InputMaybe<string>,
  milestoneTargets: ListFieldState<MilestoneTargetRow>,
  globalPlanMilestones: ScheduleTab_GlobalPlanMilestoneFragment[],
) {
  if (!isDefined(globalPlanMilestoneId)) return;

  const milestone = globalPlanMilestones.find((m) => m.id === globalPlanMilestoneId);
  const deletedRow = milestoneTargets.rows.find(
    (targetRow) =>
      targetRow !== row &&
      targetRow.op.value === IncrementalCollectionOp.Delete &&
      targetRow.globalPlanMilestoneId.value === globalPlanMilestoneId,
  );

  if (deletedRow) {
    // Revive the deleted row instead of adding a duplicate
    deletedRow.set({
      op: IncrementalCollectionOp.Include,
      globalPlanMilestoneName: milestone?.name,
      ...(isDefined(row.targetDaysFromRts.value) ? { targetDaysFromRts: row.targetDaysFromRts.value } : {}),
    });
    // And discard the newly duplicated row -> this does cause a rerender/reorder of the item, but seems fine
    milestoneTargets.remove(row.value);
    return;
  }

  row.set({ globalPlanMilestoneId, globalPlanMilestoneName: milestone?.name });
}

function removeRow(milestoneTargets: ListFieldState<MilestoneTargetRow>, row: ObjectState<MilestoneTargetRow>) {
  if (row.id.originalValue) {
    // Persisted row: soft-delete so the backend receives a Delete op on save
    row.op.set(IncrementalCollectionOp.Delete);
  } else {
    // Unsaved row: drop from the list entirely
    milestoneTargets.remove(row.value);
  }
}

function getMilestoneOptions(
  row: ObjectState<MilestoneTargetRow>,
  milestoneTargets: ListFieldState<MilestoneTargetRow>,
  globalPlanMilestones: ScheduleTab_GlobalPlanMilestoneFragment[],
) {
  const selectedByOthers = new Set(
    milestoneTargets.rows
      .filter((other) => other !== row && other.op.value !== IncrementalCollectionOp.Delete)
      .map((other) => other.globalPlanMilestoneId.value)
      .filter(isDefined),
  );
  return globalPlanMilestones.filter(
    (milestone) => milestone.id === row.globalPlanMilestoneId.value || !selectedByOthers.has(milestone.id),
  );
}

function getScaledCommunityTargetDaysFromRts(
  row: ObjectState<MilestoneTargetRow>,
  targetCycleTimeInDays: InputMaybe<number>,
  globalPlanMilestones: ScheduleTab_GlobalPlanMilestoneFragment[],
  communityVerticalCompleteTdf: InputMaybe<number>,
) {
  // Scaling applies to construction milestones, excluding Sales Handoff and Vertical Complete.
  if (
    getMilestoneType(row.globalPlanMilestoneId.value!, globalPlanMilestones)?.code !==
    GlobalPlanMilestoneType.Construction
  ) {
    return undefined;
  }
  if (isSalesReadyMilestone(row, globalPlanMilestones) || isVerticalCompleteMilestone(row, globalPlanMilestones)) {
    return undefined;
  }

  const communityTargetDaysFromRts = row.communityTargetDaysFromRts.value;
  if (
    !isDefined(communityTargetDaysFromRts) ||
    !isDefined(communityVerticalCompleteTdf) ||
    communityVerticalCompleteTdf === 0 ||
    !isDefined(targetCycleTimeInDays)
  ) {
    return undefined;
  }
  // Structure VC TDF stays synced with TCT, so TCT is the structure-side scale base.
  return Math.ceil((communityTargetDaysFromRts / communityVerticalCompleteTdf) * targetCycleTimeInDays);
}

/**
 * Construction (except Sales Ready / Vertical Complete) uses the VC-ratio-scaled default.
 * Vertical Complete defaults to the structure TCT. Precon / Sales Ready use the raw community value.
 */
function getCommunityDefaultTargetDaysFromRts(
  row: ObjectState<MilestoneTargetRow>,
  targetCycleTimeInDays: InputMaybe<number>,
  globalPlanMilestones: ScheduleTab_GlobalPlanMilestoneFragment[],
  communityVerticalCompleteTdf: InputMaybe<number>,
) {
  if (isVerticalCompleteMilestone(row, globalPlanMilestones)) {
    return targetCycleTimeInDays;
  }
  if (
    getMilestoneType(row.globalPlanMilestoneId.value!, globalPlanMilestones)?.code !==
      GlobalPlanMilestoneType.Construction ||
    isSalesReadyMilestone(row, globalPlanMilestones)
  ) {
    return row.communityTargetDaysFromRts.value;
  }
  return getScaledCommunityTargetDaysFromRts(
    row,
    targetCycleTimeInDays,
    globalPlanMilestones,
    communityVerticalCompleteTdf,
  );
}

function scaleMilestoneTargetsToCycleTime(
  milestoneTargets: ListFieldState<MilestoneTargetRow>,
  targetCycleTimeInDays: InputMaybe<number>,
  globalPlanMilestones: ScheduleTab_GlobalPlanMilestoneFragment[],
) {
  const communityVerticalCompleteTdf = getCommunityVerticalCompleteTdf(milestoneTargets.rows, globalPlanMilestones);

  // VC TDF is always the structure TCT (not ratio-scaled, not skipped).
  milestoneTargets.rows.forEach((row) => {
    if (row.op.value === IncrementalCollectionOp.Delete) return;
    if (isVerticalCompleteMilestone(row, globalPlanMilestones) && isDefined(targetCycleTimeInDays)) {
      row.targetDaysFromRts.set(targetCycleTimeInDays);
    }
  });

  milestoneTargets.rows.forEach((row) => {
    if (row.op.value === IncrementalCollectionOp.Delete || row.targetDaysFromRtsSetByUser.value) {
      return;
    }
    const scaledDefault = getScaledCommunityTargetDaysFromRts(
      row,
      targetCycleTimeInDays,
      globalPlanMilestones,
      communityVerticalCompleteTdf,
    );
    if (isDefined(scaledDefault)) {
      row.targetDaysFromRts.set(scaledDefault);
    }
  });
}

function resetRowToScaledCommunityDefault(
  row: ObjectState<MilestoneTargetRow>,
  targetCycleTimeInDays: InputMaybe<number>,
  globalPlanMilestones: ScheduleTab_GlobalPlanMilestoneFragment[],
  communityVerticalCompleteTdf: InputMaybe<number>,
) {
  if (isVerticalCompleteMilestone(row, globalPlanMilestones)) return;

  const communityDefault = getCommunityDefaultTargetDaysFromRts(
    row,
    targetCycleTimeInDays,
    globalPlanMilestones,
    communityVerticalCompleteTdf,
  );
  if (isDefined(communityDefault)) {
    row.targetDaysFromRts.set(communityDefault);
    row.targetDaysFromRtsSetByUser.set(false);
  }
}

// Used to keep a stable identity of the row through the pre-create vs post save lifecycle
const pendingRowId = "pending";
function getGridRowId(row: ObjectState<MilestoneTargetRow>): string {
  return row.globalPlanMilestoneId.value ?? row.id.value ?? pendingRowId;
}

function hasPendingRow(rows: readonly ObjectState<MilestoneTargetRow>[]) {
  return rows.some((row) => !row.globalPlanMilestoneId.value);
}

function isWeekend(day: Temporal.PlainDate) {
  return day.dayOfWeek === 6 || day.dayOfWeek === 7;
}

const planScheduleFormConfig: ObjectConfig<PlanScheduleFormValue> = {
  id: { type: "value", rules: [required] },
  targetStartDate: { type: "value", rules: [required] },
  targetCycleTimeInDays: { type: "value", rules: [required] },
  currentBaselineDate: { type: "value", rules: [required] },
  targetConstructionRtsDate: { type: "value" },
  targetPreconRtsDate: { type: "value" },
  milestoneTargets: {
    type: "list",
    config: {
      id: { type: "value" },
      globalPlanMilestoneId: { type: "value", rules: [required] },
      globalPlanMilestoneName: { type: "value" },
      targetDaysFromRts: { type: "value", rules: [required] },
      targetDaysFromRtsSetByUser: { type: "value" },
      op: { type: "value" },
      communityTargetDaysFromRts: { type: "value" },
      communityTargetCycleTimeInDays: { type: "value" },
      currentEndDate: { type: "value" },
    },
  },
};
